'use client';

import { Row } from '@vuddy/components';
import { theme } from '@vuddy/components/theme';
import { isNonNil, jsonAssignInlineVars } from '@vuddy/utils';
import { motion } from 'framer-motion';
import {
  Children,
  CSSProperties,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';
import * as styles from './tabs.css';
import { IUseTabsOptions, useTabs } from './useTabs';
import { TabsContext } from './useTabsContext';

interface TabsProps extends IUseTabsOptions {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export const Tabs = ({
  children,
  defaultIndex = 0,
  activeColor,
  inactiveColor,
  activeIndex,
  onChangeIndex,
  className,
  style,
  activeTabStyle,
  inactiveTabStyle,
}: TabsProps) => {
  const tabs = useTabs({
    defaultIndex,
    activeColor,
    inactiveColor,
    activeIndex,
    onChangeIndex,
    activeTabStyle,
    inactiveTabStyle,
  });
  const { renderedIndexList, borderColor } = tabs;

  const tabElements = Children.toArray(children)
    .filter(isValidElement)
    .map((tabElement: ReactElement<any>) => ({
      titleElement: tabElement,
      contentsElement: tabElement.props.children,
    })); // ReactNode 타입 자체가 nullable 하므로 filter를 해도 하단에선 null처리르 해줘야 함

  return (
    <TabsContext.Provider value={tabs}>
      <Row
        role="tablist"
        className={className}
        style={style}
        borderBottomWidth="stroke_base"
        borderBottomStyle="solid"
        borderBottomColor={borderColor.inactive}
        position="relative">
        {tabElements.map(({ titleElement }) => titleElement)}
        <motion.div
          className={styles.activeBorder}
          animate="move"
          variants={{
            move: (activeIndex: number) => ({
              x: `calc(${activeIndex} * 100%)`,
              transition: { ease: 'easeInOut' },
            }),
          }}
          custom={tabs.activeIndex}
          style={jsonAssignInlineVars({
            [styles.tabLength]: tabElements.length,
            [styles.borderColor]: theme.line[borderColor.active],
          })}
        />
      </Row>
      {renderedIndexList.map(renderedIndex =>
        isNonNil(tabElements[renderedIndex]?.contentsElement) ? (
          <div
            className={styles.tabPanel({
              isActive: renderedIndex === tabs.activeIndex,
            })}
            key={renderedIndex}
            role="tabpanel">
            {tabElements[renderedIndex]?.contentsElement}
          </div>
        ) : null
      )}
    </TabsContext.Provider>
  );
};
